/**
 * Error Handler Test Panel
 * 
 * ⚠️ TESTING ONLY - REMOVE BEFORE PRODUCTION ⚠️
 * 
 * This component provides a UI to test all error handling scenarios.
 * 
 * Usage:
 * 1. Navigate to /test-errors
 * 2. Click buttons to test different error scenarios
 * 3. Check top-right corner for notifications
 * 4. Check browser console (F12) for error logs
 * 
 * IMPORTANT: 
 * - This component is already added to App.js routes
 * - Remove the route from App.js when testing is complete
 * - See REMOVE_TEST_PANEL.md for removal instructions
 */

import React from 'react';
import { useErrorHandler } from '../hooks/useErrorHandler';
import errorNotificationService from '../services/ErrorNotificationService';

const ErrorHandlerTestPanel = () => {
  const { 
    handleError, 
    handleSubscriptionError, 
    handleValidationError, 
    handleFirebaseError, 
    handleNetworkError 
  } = useErrorHandler();

  const testSubscriptionExpired = () => {
    const error = new Error('Your subscription has expired. Please renew your plan to continue.');
    error.code = 'SUBSCRIPTION_EXPIRED';
    error.daysRemaining = -5;
    handleSubscriptionError(error, {
      context: 'TestPanel',
      title: 'Subscription Expired'
    });
  };

  const testSubscriptionExpiring = () => {
    const error = new Error('Your subscription expires soon. Please renew your plan.');
    error.code = 'SUBSCRIPTION_EXPIRED';
    error.daysRemaining = 3;
    handleSubscriptionError(error, {
      context: 'TestPanel',
      title: 'Subscription Expiring'
    });
  };

  const testOperationLimitExceeded = () => {
    const error = new Error('Monthly operation limit exceeded! You\'ve used 10000 of 10000 operations.');
    error.code = 'OPERATION_LIMIT_EXCEEDED';
    error.limits = {
      operations: { current: 10000, limit: 10000 }
    };
    handleSubscriptionError(error, {
      context: 'TestPanel',
      title: 'Operation Limit Reached'
    });
  };

  const testStorageLimitExceeded = () => {
    const error = new Error('Storage limit exceeded! You\'ve used 500MB of 500MB.');
    error.code = 'STORAGE_LIMIT_EXCEEDED';
    error.limits = {
      storage: { current: 500, limit: 500 }
    };
    handleSubscriptionError(error, {
      context: 'TestPanel',
      title: 'Storage Limit Reached'
    });
  };

  const testValidationError = () => {
    const error = new Error('Company name is required');
    error.code = 'VALIDATION_ERROR';
    error.fieldName = 'firmName';
    handleValidationError(error, {
      context: 'TestPanel',
      fieldName: 'firmName',
      title: 'Validation Error'
    });
  };

  const testMultipleValidationErrors = () => {
    const errors = [
      { field: 'firmName', message: 'Company name is required' },
      { field: 'companyType', message: 'Company type is required' },
      { field: 'contactPerson', message: 'Contact person is required' }
    ];

    errors.forEach(({ field, message }) => {
      const error = new Error(message);
      error.code = 'VALIDATION_ERROR';
      error.fieldName = field;
      handleValidationError(error, {
        context: 'TestPanel',
        fieldName: field,
        title: 'Validation Error'
      });
    });
  };

  const testFirebasePermissionDenied = () => {
    const error = new Error('Permission denied');
    error.code = 'permission-denied';
    handleFirebaseError(error, {
      context: 'TestPanel',
      title: 'Firebase Error'
    });
  };

  const testFirebaseAuthError = () => {
    const error = new Error('Invalid credentials');
    error.code = 'auth/invalid-credential';
    handleFirebaseError(error, {
      context: 'TestPanel',
      title: 'Authentication Error'
    });
  };

  const testNetworkError = () => {
    const error = new Error('Network request failed');
    error.code = 'NETWORK_ERROR';
    handleNetworkError(error, {
      context: 'TestPanel',
      title: 'Network Error'
    });
  };

  const testTimeoutError = () => {
    const error = new Error('Request timed out');
    error.message = 'Request timed out. Please try again.';
    handleNetworkError(error, {
      context: 'TestPanel',
      title: 'Timeout Error'
    });
  };

  const testGenericError = () => {
    const error = new Error('An unexpected error occurred');
    handleError(error, {
      context: 'TestPanel',
      title: 'Generic Error'
    });
  };

  const testErrorBoundary = () => {
    // This will trigger the error boundary
    throw new Error('Test error boundary - this should be caught by ErrorBoundary');
  };

  const testDefensiveInitialization = () => {
    // Temporarily clear initialization to test defensive behavior
    const originalInit = errorNotificationService.isInitialized;
    errorNotificationService.isInitialized = false;
    
    const error = new Error('Test defensive initialization');
    errorNotificationService.handleError(error, {
      context: 'TestPanel'
    });
    
    // Restore
    errorNotificationService.isInitialized = originalInit;
    console.log('Check console for "ErrorNotificationService not initialized" warning');
  };

  const testPersistentNotification = () => {
    const error = new Error('This is a persistent notification that won\'t auto-dismiss');
    handleError(error, {
      context: 'TestPanel',
      title: 'Persistent Error',
      duration: 0 // Persistent
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Error Handler Test Panel
          </h1>
          <p className="text-gray-600 mb-4">
            Click buttons below to test different error scenarios. Check the top-right corner for notifications.
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              <strong>⚠️ Warning:</strong> This component is for testing only. Remove before production deployment.
            </p>
          </div>
        </div>

        {/* Subscription Errors */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription Errors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={testSubscriptionExpired}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Test Subscription Expired
            </button>
            <button
              onClick={testSubscriptionExpiring}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Test Subscription Expiring (3 days)
            </button>
            <button
              onClick={testOperationLimitExceeded}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
            >
              Test Operation Limit Exceeded
            </button>
            <button
              onClick={testStorageLimitExceeded}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
            >
              Test Storage Limit Exceeded
            </button>
          </div>
        </div>

        {/* Validation Errors */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Validation Errors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={testValidationError}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Test Single Validation Error
            </button>
            <button
              onClick={testMultipleValidationErrors}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Test Multiple Validation Errors
            </button>
          </div>
        </div>

        {/* Firebase Errors */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Firebase Errors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={testFirebasePermissionDenied}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Test Permission Denied
            </button>
            <button
              onClick={testFirebaseAuthError}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Test Auth Error
            </button>
          </div>
        </div>

        {/* Network Errors */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Network Errors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={testNetworkError}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Test Network Error
            </button>
            <button
              onClick={testTimeoutError}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Test Timeout Error
            </button>
          </div>
        </div>

        {/* Other Tests */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Other Tests</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={testGenericError}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Test Generic Error
            </button>
            <button
              onClick={testPersistentNotification}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Test Persistent Notification
            </button>
            <button
              onClick={testDefensiveInitialization}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Test Defensive Initialization
            </button>
            <button
              onClick={testErrorBoundary}
              className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
            >
              Test Error Boundary (Will Crash)
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Testing Instructions</h3>
          <ol className="list-decimal list-inside space-y-2 text-blue-800">
            <li>Click any button above to trigger an error scenario</li>
            <li>Check the top-right corner for notification appearance</li>
            <li>Check browser console (F12) for error logs</li>
            <li>Verify notification content, icon, and duration</li>
            <li>Test dismissing notifications (click X or wait for auto-dismiss)</li>
            <li>Test multiple notifications stacking</li>
            <li>Verify error messages are user-friendly (not technical)</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ErrorHandlerTestPanel;

